import React from "react";
import withStyles from "isomorphic-style-loader/lib/withStyles";
import classNames from "classnames/bind";
import PropTypes from "prop-types";
import { Container, Row, Col, Button, Jumbotron } from "reactstrap";
import { canUseDOM } from "fbjs/lib/ExecutionEnvironment";

import playSound from "~/utils/playSound";
import { getCookie, saveCookie } from "~/utils/cookie";
import assetPath from "~/utils/assetPath";
import {
  isProductionEnvironment,
  isDesktopApp,
  isGamedistributionApp
} from "~/utils/misc";
import {
  canvasContainer,
  gameContainer
} from "./helpers/elements";
import PurchaseModalComponent from "~/components/PurchaseModalComponent";
import {
  scaledDimension
} from "./helpers/utils";

import styles from "./styles.scss";

class Puzzle extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    isGameActive: PropTypes.bool.isRequired,
    isEdgesActive: PropTypes.bool.isRequired,
    isPreviewActive: PropTypes.bool.isRequired,
    startNewGame: PropTypes.func,
    togglePreview: PropTypes.func
  }
  static contextTypes = {
    fetch: PropTypes.func.isRequired
  };

  constructor(props) {
    // Set props
    super(props);

    // Set initial state
    this.state = {
      isLoadingPictures: false,
      isGameStarting: false,
      picture: null,
      game: null,
      amountOfPiecesDropdownOpen: false,
      offsetRight: 0
    };

    // Bind element functions
    this.canvasContainer = canvasContainer.bind(this);
    this.gameContainer = gameContainer.bind(this);
  }

  //-----------------------------------------------------------------------
  // Lifecycle
  //-----------------------------------------------------------------------
  componentDidMount() {
    emitter.on("freeGameRequested", this.handleFreeGameRequested);
    emitter.on("toggleEdgesComplete", this.handleToggleEdges);
    emitter.on("togglePreviewComplete", this.handleTogglePreview);
    emitter.on("shuffleTapped", this.handleShuffleTapped);
    emitter.on("windowResizeEvent", this.handleWindowResize);

    if (bowser.mobile) this.handleWindowResize()

    this.loadPicture()
  }

  shouldComponentUpdate() {
    return false;
  }

  //-----------------------------------------------------------------------
  // Handlers
  //-----------------------------------------------------------------------
  handleWindowResize = () => {
    this.forceUpdate();

    if (bowser.mobile && window.innerHeight > window.innerWidth && !this.notification) {
      this.notification = $.notify("🔄 Please rotate your screen for optimal gameplay.", { delay: 320000 });
    } else if (!!this.notification) {
      this.notification.close();
      this.notification = null;
    }

    if (this.state.game) {
      const dimensions = scaledDimension(this.state.picture, this.state.offsetRight);
      this.state.game.opts.offsetTop = ((window.innerHeight - dimensions.height - (isDesktopApp() ? 0 : 48)) / 2);
      this.state.game.opts.offsetRight = this.state.offsetRight
      this.state.game.opts.maxWidth = window.innerWidth - 32
      this.state.game.opts.maxHeight = window.innerHeight - (isDesktopApp() ? 0 : 48) - 32

      this.state.game.resize();

      this.state.game.eventBus.emit(jigsaw.Events.JIGSAW_SHUFFLE);
    }
  }

  handleToggleEdges = () => {
    if (this.props.isEdgesActive) {
      this.state.game.eventBus.emit(jigsaw.Events.SHOW_EDGE);
    } else {
      this.state.game.eventBus.emit(jigsaw.Events.SHOW_ALL);
    }
  };

  handleTogglePreview = () => {
    this.state.game.eventBus.emit(jigsaw.Events.SHOW_PREVIEW);
  };

  handleShuffleTapped = () => {
    this.state.game.eventBus.emit(jigsaw.Events.JIGSAW_SHUFFLE);
  };

  handleStartGameTapped = (params = {}) => {
    // Check if purchase modal should be started
    if (
      isProductionEnvironment() &&
      isDesktopApp() &&
      is.above(getCookie("gamesPlayedAmount", 0), 30) &&
      is.falsy(getCookie("purchasedUnlimitedGames", false)) &&
      is.falsy(params.freeGame)
    ) {
      this.purchaseModalComponent.showModal();
      return;
    }

    if (bowser.mobile && window.innerHeight > window.innerWidth) {
      $.notify("NOTICE: Rotate your screen before starting game.");
      return;
    }

    this.setState({ isGameStarting: true })
    this.forceUpdate()

    const dimensions = scaledDimension(this.state.picture, this.state.offsetRight);

    this.state.game = new jigsaw.Jigsaw({
      defaultImage: this.state.picture.urls.full,
      toolbarHeight: (isDesktopApp() ? 0 : 48),
      offsetTop: ((window.innerHeight - dimensions.height - (isDesktopApp() ? 0 : 48)) / 2),
      offsetRight: this.state.offsetRight,
      maxWidth: window.innerWidth - 32,
      maxHeight: window.innerHeight - (isDesktopApp() ? 0 : 48) - 32,
      border: true,
      shuffled: false,
      rotatePieces: getCookie("settingsRotatePieces", true),
      defaultPieces: getCookie("settingsAmountOfPieces", 100),
      squarePieces: false
    });

    this.state.game.eventBus.on(jigsaw.Events.PIECES_CONNECTED, () => {
      const shouldPlaySound = getCookie("settingsSound", isDesktopApp() || isGamedistributionApp());
      if (shouldPlaySound) {
        playSound("plop");
      }
    });

    this.state.game.eventBus.on(jigsaw.Events.JIGSAW_COMPLETE, () => {
      emitter.emit("gameWasWon");
    });

    this.state.game.eventBus.on(jigsaw.Events.RENDER_FINISH, () => {
      this.props.startNewGame()
      if (is.truthy(getCookie("settingsShowPreview", true))) {
        this.props.togglePreview()
      }
      this.state.game.eventBus.emit(jigsaw.Events.JIGSAW_SHUFFLE);
    });

    saveCookie("gamesPlayedAmount", getCookie("gamesPlayedAmount", 0) + 1);
  };

  handleAmountOfPiecesDropdownTap = () => {
    this.setState({ amountOfPiecesDropdownOpen: !this.state.amountOfPiecesDropdownOpen })

    this.forceUpdate()
  };

  handleAmountOfPiecesTap = (amount) => {
    saveCookie("settingsAmountOfPieces", amount)

    this.forceUpdate()
  };

  handleRotatePiecesTap = (bool) => {
    saveCookie("settingsRotatePieces", bool)

    this.forceUpdate()
  };

  handleFreeGameRequested = () => {
    // Return if animating
    if (is.truthy(this.props.isGameAnimating)) {
      return;
    }

    // Start new game
    this.handleStartGameTapped({ freeGame: true });
  };

  //-----------------------------------------------------------------------
  // Helpers
  //-----------------------------------------------------------------------
  loadPicture = () => {
    if (is.truthy(this.state.isLoadingPicture)) {
      return
    }

    this.state.isLoadingPicture = true

    this.context.fetch(`/api/unsplash/photos/${this.props.id}`).then(response => response.json()).then((json) => {
      this.setState({ picture: json })
      this.forceUpdate();
    });
  };

  //-----------------------------------------------------------------------
  // Render
  //-----------------------------------------------------------------------
  render() {
    return (
      <div id="puzzle_component">
        {this.canvasContainer()}
        <If condition={is.truthy(canUseDOM) && is.falsy(this.state.isGameStarting) && is.falsy(this.props.isGameActive) && is.existy(this.state.picture)}>
          {this.gameContainer(this.state.picture, this.state.offsetRight)}
        </If>
        <PurchaseModalComponent
          onRef={ref => {
            this.purchaseModalComponent = ref;
          }}
        />
      </div>
    );
  }
}

export default withStyles(styles)(Puzzle);



// WEBPACK FOOTER //
// ./src/routes/puzzles/components/PuzzleComponent/index.js